import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatRadioModule } from '@angular/material/radio';
import { ExportProposalRequiredFieldDialogComponent } from '../export-proposal-required-field-dialog/export-proposal-required-field-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { ProjectTabs, PropertyOptionId } from '../../../../models/enum';
import { CommonDataService } from '../../../../services/common-data.service';
import { ForcePublishDetails, ProposalExportRequest, CollaborationHistoryEnum, CollaborationState, TaxDetailsPreferences, SetUserPreferencesRequest, ProposalSettings } from '../../../../models/dtos';
import { UserProfileService } from '../../../../services/common/user-profile.service';
import { CommonFunctionsService } from '../../../../services/common-functions.service';
import { EstimateSaveService } from '../../../../services/estimate-save.service';
import { OpenEstimateApiService, ProposalExportEvent } from '../../../../services/api/open-estimate.service';
import { SolEstimateService } from '../../../../services/estimate/sol-estimate.service';
import { Subject, takeUntil, firstValueFrom } from 'rxjs';
import { LoadingSpinnerService } from '../../../../services/common/loading-spinner.service';
import { CONSTANTS } from '../../../../Constants/Constants';
import { PreferenceApiService } from '../../../../services/api/preference.service';
import { GocCollaborationRestrictionHandlerService } from '../../../../services/common/goc-collaboration-restriction-handler.service';
import { HttpErrorResponse } from '@angular/common/http';
import { CommonMetaDataService } from '../../../../services/common-meta-data.service';

@Component({
  selector: 'app-export-proposal-dialog',
  imports: [MatButtonModule,
    TranslatePipe,
    CommonModule,
    FormsModule,
    MatRadioModule,
    MatIconModule,
  ],
  templateUrl: './export-proposal-dialog.component.html',
  styleUrl: './export-proposal-dialog.component.scss',
  standalone: true
})
export class ExportProposalDialogComponent implements OnInit, OnDestroy {

  @Output() navigateToProposalProperties = new EventEmitter<void>();
  @Output() navigateToEstimateTab = new EventEmitter<void>();
  @Output() navigateToEstimateThenProposalProperties = new EventEmitter<void>();
  @Output() closeDialogEvent = new EventEmitter<void>();

  private readonly destroy$ = new Subject<void>();

  restrictionType: 'Restricted' | 'Unrestricted' = 'Unrestricted';
  showAltContent: boolean = true;
  isRestrictionTypeSet: boolean = false;
  proposalStatus: boolean = false;
  statusMessage: string = CONSTANTS.EMPTY_STRING;
  proposalLink: string = null;
  operationStatus: string = null;
  invalidPGTAccounts: string[] = [];
  pendingInvalidPGTAccounts: string[] = [];
  errorCode: number = CONSTANTS.ZERO;
  isOpportunityLinked: boolean = false;
  private _taxPreferencesData: TaxDetailsPreferences = null;
  private proposalSettings: ProposalSettings = null;

  propertyOptionId = PropertyOptionId;
  constructor(
    private readonly dialog: MatDialog,
    private readonly commonDataService: CommonDataService,
    private readonly userProfileService: UserProfileService,
    private readonly commonFunctionsService: CommonFunctionsService,
    private readonly estimateSaveService: EstimateSaveService,
    private readonly translate: TranslateService,
    private readonly preferenceApiService: PreferenceApiService,
    private readonly openEstimateApiService: OpenEstimateApiService,
    private readonly solEstimateService: SolEstimateService,
    private readonly loadingSpinnerService: LoadingSpinnerService,
    private readonly gocCollaborationRestrictionHandlerService: GocCollaborationRestrictionHandlerService,
    public metadataService: CommonMetaDataService
  ) { }


  ngOnInit(): void {
    this.proposalSettings = this.commonDataService.getOtherMetaData().proposalSettings;
    this.isOpportunityLinked = !!this.commonDataService.getEstimateMetaData().estimateSettings?.opportunityId;
    if (this.isOpportunityLinked || this.proposalSettings?.isOpportunityRestrictionTypeSelected) {
      this.isRestrictionTypeSet = true;
    } else {
      this.isRestrictionTypeSet = this.proposalSettings?.isOpportunityRestrictionTypeSelected;
    }
    this.restrictionType = this.proposalSettings?.isRestrictedOpportunity ? 'Restricted' : 'Unrestricted';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onExportToProposal() {
    if (this.isRestrictionTypeSet || !this.showAltContent) {
      if (this.restrictionType) {
        this.isRestrictionTypeSet = true;
        if (!this.isOpportunityLinked) {
          this.setRestrictionTypeInProposalSettings();
        }
        const preferences = this.commonDataService.getPreferenceData()?.preferences;
        if (preferences?.salesPersonName && preferences?.salesPersonEmail) {
          this.exportToProposal();
        } else {
          this.openRequiredFieldsDialog();
        }
        this.showAltContent = true;
      }
    } else {
      this.showAltContent = false;
    }
  }

  private openRequiredFieldsDialog() {
    this.dialog.open(ExportProposalRequiredFieldDialogComponent, {
      width: '500px',
      data: { restrictionType: this.restrictionType },
      disableClose: true
    }).afterClosed().pipe(
      takeUntil(this.destroy$)
    ).subscribe(async result => {
      if (result?.name && result?.email) {
        const preferenceData = this.commonDataService.getPreferenceData();
        const updatedPreferenceData = {
          ...preferenceData,
          preferences: {
            ...preferenceData?.preferences,
            salesPersonName: result.name,
            salesPersonEmail: result.email
          }
        };

        const request: SetUserPreferencesRequest = {
          userId: this.userProfileService.userInfo?.gid ?? CONSTANTS.EMPTY_STRING,
          preferences: updatedPreferenceData.preferences,
          firePreferences: null,
          esPreferences: null,
          proposalPreferences: null,
          collaboratorPreferences: null,
          costReviewerPreferences: null,
          taxDetailsPreferences: null,
          resetPreferencesToDefault: false
        };

        const isSaved = await firstValueFrom(this.preferenceApiService.setPreference(request));
        if (isSaved) {
          preferenceData.preferences.salesPersonName = result.name;
          preferenceData.preferences.salesPersonEmail = result.email;
          this.commonDataService.setPreferenceData(preferenceData);
          this.exportToProposal();
        }
      }
    });
  }

  openSolEstimate() {
    if (this.isReviewOrReportTabSelected()) {
      this.navigateToEstimateThenProposalProperties.emit();
      return;
    }
    this.navigateToProposalProperties.emit();
  }

  private isReviewOrReportTabSelected(): boolean {
    const activeTab = this.commonDataService.getActiveTab();
    return activeTab === ProjectTabs.Review || activeTab === ProjectTabs.Reports;
  }

  onOkClick() {
    this.navigateToEstimateTab.emit();
  }

  onResendWithInvalidPGT() {
    this.pendingInvalidPGTAccounts = [...this.invalidPGTAccounts];
    this.invalidPGTAccounts = [];
    this.exportToProposal();
  }

  onCancelInvalidPGT() {
    this.invalidPGTAccounts = [];
    this.pendingInvalidPGTAccounts = [];
    this.showAltContent = true;
  }

  hideRestrictionType() {
    this.showAltContent = true;
    this.isRestrictionTypeSet = false;
    this.restrictionType = 'Unrestricted';
    this.proposalSettings.isRestrictedOpportunity = false;
    this.proposalSettings.isOpportunityRestrictionTypeSelected = false;
    this.invalidPGTAccounts = [];
    this.pendingInvalidPGTAccounts = [];
    this.errorCode = CONSTANTS.ZERO;
  }

  onRestrictionTypeChange(value: 'Restricted' | 'Unrestricted') {
    this.restrictionType = value;
    this.isRestrictionTypeSet = true;
    this.setRestrictionTypeInProposalSettings();
  }

  private setRestrictionTypeInProposalSettings(): void {
    this.proposalSettings.isOpportunityRestrictionTypeSelected = true;
    this.proposalSettings.isRestrictedOpportunity = this.restrictionType === 'Restricted';
  }

  private downloadProposalXml(proposalXml: string): void {
    const blob = new Blob([proposalXml], { type: 'application/xml' });
    const url = globalThis.URL.createObjectURL(blob);
    const link = document.createElement('a');
    const estimateId = this.commonDataService.getEstimateId() || 'proposal';
    link.href = url;
    link.download = `Proposal_${estimateId}.xml`;
    link.click();
    globalThis.URL.revokeObjectURL(url);
  }

  async exportToProposal() {
    const estimateData = this.commonDataService.getEstimateData();
    const isFirstProposalGenerated = !estimateData.proposalID;
    this.solEstimateService.ensureModelPopulated();
    const estimateModel = this.solEstimateService.getModel();
    this.loadingSpinnerService.show(CONSTANTS.DASHBOARD_SPINNER);


    const exportToProposalRequest: ProposalExportRequest = {
      estimateType: estimateData.estimateTypeId,
      business:  this.commonFunctionsService.getBusinessType(estimateData.business),
      estimateStatus: estimateData.estimateStatusId,
      isQuotedServiceEstimate: this.commonFunctionsService.isQuotedServiceEstimate(),
      isArchive: estimateData.isArchived,
      materialSellPrice: estimateModel.materialCosts.materialCostsHeader.sellPrice,
      laborSellPrice: estimateModel.laborCosts.laborCostsHeader.sellPrice,
      isReviewTabVisible: this.commonDataService.getReviewTabVisibility(),
      isOptionalGroupboxCheckBox: false, //estimateData.isOptionalGroupboxCheckBox,
      sellPriceProposalTaskID: estimateData.sellPriceProposalTaskID,
      isCostReviewApproved: this.commonFunctionsService.getCostReviewStatusName(this.commonDataService.getCostReviewData()) === 'Approved',
      areaIdList: this.commonDataService.proposalAdvancedOptionsAreaList.map(area => area.id),
      invalidPGTAccounts: this.pendingInvalidPGTAccounts,
      collaborators: this.commonDataService.getEstimateOpenResponse()?.collaborationData?.collaboratorDataList || [],
      typicalOfHelpers: this.getTypicalOfHelpersRecord(),
      forcePublishDetails: this.getForcePublishDetails(),
      estimateSaveRequest: await this.estimateSaveService.createSaveEstimateRequest()
    }
    this.showAltContent = false;
    this.operationStatus = null;
    this.errorCode = CONSTANTS.ZERO;
    this.statusMessage = CONSTANTS.EMPTY_STRING;
    this.openEstimateApiService.exportToProposal(exportToProposalRequest).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (event: ProposalExportEvent) => {
        if (event.type === 'status') {
          this.operationStatus = event.status;
          this.loadingSpinnerService.updateMessage(event.status);
        } else if (event.type === 'result') {
          const response = event.result;
          
          // Check if there's an error code
          if (response.errorCode && response.errorCode !== CONSTANTS.ZERO) {
            this.proposalLink = null;
            this.operationStatus = null;
            this.hideSpinner();
            if (response.errorCode === CONSTANTS.ERROR_FOUR_TWO_TWO && response.invalidPGTAccounts?.length > 0) {
              this.invalidPGTAccounts = response.invalidPGTAccounts;
              this.showAltContent = true;
              return;
            }
            this.proposalStatus = true;
            this.errorCode = response.errorCode;
            this.statusMessage = this.getErrorMessage(response.errorCode);
            return;
          }
          
          if (response.proposalLink !== null && response.proposalLink !== undefined) {
            this.proposalLink = response.proposalLink;
            this.statusMessage = this.translate.instant('EXPORT_TO_PROPOSAL_SUCCESS_MESSAGE');
            this.commonDataService.patchEstimate({
              proposalID: response.proposalID,
              proposalLink: response.proposalLink,
              lastProposalGeneratedDate: response.lastProposalGenerated,
              proposalTaskID: response.proposalTaskID,
              sellPriceProposalTaskID: response.sellPriceProposalTaskID
            });
            
            if (isFirstProposalGenerated) {
              this.commonDataService.isSaveEnabled = false;
              if (this.commonDataService.isCollaborationEstimate() && response.collaborationHubData) {
                const collaborationHubData = response.collaborationHubData;
                this.commonFunctionsService.updateCollaborationHubData(collaborationHubData);
              }
            }
          }
          if (response.proposalXml) {
            this.downloadProposalXml(response.proposalXml);
          }
          this.proposalStatus = true;
          this.operationStatus = null;
          this.hideSpinner();
        }
      },
      error: async (error: unknown) => {
        await this.handleExportFailure(error);
      }
    });
  }

  private async handleExportFailure(error: unknown): Promise<void> {
    const httpError = error instanceof HttpErrorResponse ? error : null;
    const errorCode = this.extractSQErrorCodeFromHttpError(httpError);
    const httpErrorCode = httpError?.status ?? null;
    
    this.hideSpinner();
    this.proposalLink = null;
    this.proposalStatus = true;

    if (httpError && errorCode && this.gocCollaborationRestrictionHandlerService.canHandle(errorCode.toString())) {
      if(await this.gocCollaborationRestrictionHandlerService.isGocUserRestricted(httpError)){
        return;
      }
    }

    this.statusMessage = this.extractErrorDetail(httpError) ?? this.translate.instant('PROPOSAL_DEFAULT_ERROR_MESSAGE');

    if (httpErrorCode) {
      this.errorCode = httpErrorCode;
      this.statusMessage = this.getErrorMessage(httpErrorCode, errorCode);
    }
    this.operationStatus = null;
  }

  private hideSpinner(): void {
    this.loadingSpinnerService.updateMessage('');
    this.loadingSpinnerService.hide(CONSTANTS.DASHBOARD_SPINNER);
  }

  private extractSQErrorCodeFromHttpError(error: HttpErrorResponse | null): number | null {
    const raw = error?.error?.errorCode;

    if (typeof raw === 'number') {
      return Number.isInteger(raw) ? raw : null;
    }

    if (typeof raw === 'string') {
      const parsed = Number(raw.trim());
      return Number.isInteger(parsed) ? parsed : null;
    }

    return null;
  }

  private extractErrorDetail(error: HttpErrorResponse | null): string | undefined {
    const raw = error?.error?.detail;
    return typeof raw === 'string' && raw.trim().length > 0 ? raw : undefined;
  }

  getTypicalOfHelpersRecord(): Record<string, number> {
    const record: Record<string, number> = {};
    const typicalOfHelper = this.commonDataService.getTypicalOfHelpers();
    Object.keys(typicalOfHelper).forEach((key) => {
      const val = typicalOfHelper[key];
      record[key] = val.typicalOfSystems?.length ?? CONSTANTS.ONE;
    });
    return record;
  }

  getForcePublishDetails(): ForcePublishDetails | null {
    const openEstimateResponse = this.commonDataService.getEstimateOpenResponse();
    const collaborationState = openEstimateResponse?.collaborationData?.collaborationState;
    if (collaborationState !== undefined && collaborationState !== CollaborationState.NonCollaborative) {
      return {
        forcePublishAdditionalInfo: CONSTANTS.ESTIMATE_FORCE_PUBLISH_WHILE_EXPORTING_PROPOSAL,
        operation: CollaborationHistoryEnum.ProposalGenerated,
        modificationNotes: CONSTANTS.EMPTY_STRING,
        estimateCheckoutAdditionalInfo: CONSTANTS.ESTIMATE_ADD_ON_PROPOSAL_EXPORT,
        isEstimateBeingCheckedOutAfterPublish: true
      };
    }
    return null;
  }

  private getErrorMessage(errorCode: number, sqErrorCode: number | null = null): string {
    switch (errorCode) {
      case CONSTANTS.ERROR_FOUR_ZERO_ONE:
      case CONSTANTS.ERROR_FOUR_ZERO_FOUR:
      case CONSTANTS.ERROR_FOUR_ZERO_SIX:
      case CONSTANTS.ERROR_FOUR_ZERO_SEVEN:
        return this.translate.instant(`PROPOSAL_${errorCode}_ERROR_MESSAGE`);
      case CONSTANTS.ERROR_FOUR_TWO_TWO:
        if (sqErrorCode === CONSTANTS.PROPOSAL_PUBLISHED_UPDATE_ERROR) {
          return this.translate.instant('PROPOSAL_422_PUBLISHED_ERROR_MESSAGE');
        }
        return this.translate.instant(sqErrorCode === CONSTANTS.PROPOSAL_INVALID_PGT_ACCOUNTS_ERROR
          ? 'PROPOSAL_422_ERROR_MESSAGE'
          : 'PROPOSAL_DEFAULT_ERROR_MESSAGE');
      default:
        return this.translate.instant('PROPOSAL_DEFAULT_ERROR_MESSAGE');
    }
  }
  getAccountName(account: string): string {
    const collaborators = this.commonDataService.getCollaboratorData();
    const collaborator = collaborators.find(c => c.email === account);
    return collaborator ? collaborator.name : '';
  }

}
